import { useLayoutEffect } from 'react';
import type { MutableRefObject, RefObject } from 'react';
import type { ScoreTimeline, PlaybackState } from '@/types';
import { computeScrollLeft, type ScrollAnchor } from '@/utils/notationCursor';

interface UseNotationCursorOptions {
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  contentRef: RefObject<HTMLDivElement | null>;
  measureXMapRef: MutableRefObject<Map<number, number>>;
  beatAnchorsRef?: MutableRefObject<ScrollAnchor[]>;
  timeline: ScoreTimeline | null | undefined;
  currentTime: number;
  playbackState: PlaybackState;
  isDragging?: boolean;
}

export function useNotationCursor({
  scrollContainerRef,
  contentRef,
  measureXMapRef,
  beatAnchorsRef,
  timeline,
  currentTime,
  playbackState,
  isDragging,
}: UseNotationCursorOptions): { showCursor: boolean } {
  useLayoutEffect(() => {
    // OTO-26: an active user drag owns scrollLeft; autoscroll must not fight it.
    if (isDragging) return;
    if (playbackState === 'stopped') return;

    const container = scrollContainerRef.current;
    const contentEl = contentRef.current;
    if (!container || !contentEl || !timeline) return;

    container.scrollLeft = computeScrollLeft({
      currentTime,
      measures: timeline.measures,
      totalDuration: timeline.totalDuration,
      measureXMap: measureXMapRef.current,
      contentScrollWidth: contentEl.scrollWidth,
      containerWidth: container.clientWidth,
      beatAnchors: beatAnchorsRef?.current,
    });
  }, [
    currentTime,
    playbackState,
    scrollContainerRef,
    contentRef,
    measureXMapRef,
    beatAnchorsRef,
    timeline,
    isDragging,
  ]);

  return { showCursor: playbackState !== 'stopped' };
}
